// src/api/user-service/adminService.js
import client from '../client';

// ==================== Admin Dashboard ====================

/**
 * Get admin dashboard statistics (Admin only)
 * @returns {Promise<Object>} Dashboard statistics
 */
export const getAdminDashboard = async () => {
  return client.get('/api/admin/dashboard');
};

// ==================== Admin Farm Management ====================

/**
 * Get all farms with filters (Admin)
 * @param {Object} query - Query parameters { page, limit, ownerUserId, status }
 * @returns {Promise<Object>} List of farms
 */
export const getAllFarmsAdmin = async (query, axiosConfig = {}) => {
  return client.get('/api/admin/farms', { params: query, ...axiosConfig });
};

/**
 * Get unassigned farms (farms with no ownerUserId)
 * @param {Object} query - Query parameters { page, limit }
 * @returns {Promise<Object>} List of unassigned farms
 */
export const getUnassignedFarms = async (query) => {
  return client.get('/api/admin/farms/unassigned', { params: query });
};

/**
 * Get farm details by ID (Admin)
 * @param {number} farmId - Farm ID
 * @returns {Promise<Object>} Farm details
 */
export const getFarmByIdAdmin = async (farmId) => {
  return client.get(`/api/admin/farms/${farmId}`);
};

/**
 * Update farm information (Admin)
 * @param {number} farmId - Farm ID
 * @param {Object} data - Farm data { name, address, description, status, ... }
 * @returns {Promise<Object>} Updated farm data
 */
export const updateFarmAdmin = async (farmId, data) => {
  return client.patch(`/api/admin/farms/${farmId}`, data);
};

/**
 * Assign or change farm owner (Admin only)
 * @param {number} farmId - Farm ID
 * @param {number} ownerUserId - User ID with FARMER role
 * @returns {Promise<Object>} Updated farm data
 */
export const assignFarmOwner = async (farmId, ownerUserId) => {
  return client.patch(`/api/admin/farms/${farmId}/owner`, { ownerUserId });
};



// ============================================================
// Farm SEO — per-farm endpoints
// ============================================================

/**
 * Get SEO fields for all farms, paginated (Admin)
 * GET /api/admin/farms/seo/list
 * NOTE: 'farms/seo/list' must be called before 'farms/:id' on the backend (route order)
 * @param {{ page?: number, limit?: number }} query - page(default:1), limit(default:20)
 * @returns {Promise<Object>} Paginated SEO list for all farms
 */
export const getAllFarmsSeoAdmin = async ({ page = 1, limit = 20 } = {}) => {
  return client.get('/api/admin/farms/seo/list', { params: { page, limit } });
};

/**
 * Get SEO fields for a single farm (Admin)
 * GET /api/admin/farms/:id/seo
 * @param {number} farmId
 * @returns {Promise<{ metaTitle: string, metaDescription: string }>}
 */
export const getFarmSeoByIdAdmin = async (farmId) => {
  return client.get(`/api/admin/farms/${farmId}/seo`);
};

/**
 * Update SEO fields for a single farm (Admin)
 * PATCH /api/admin/farms/:id/seo
 * @param {number} farmId
 * @param {{ metaTitle?: string, metaDescription?: string }} dto
 * @returns {Promise<{ metaTitle: string, metaDescription: string }>}
 */
export const updateFarmSeoAdmin = async (farmId, dto) => {
  return client.patch(`/api/admin/farms/${farmId}/seo`, dto);
};

// ============================================================
// Experience SEO
// ============================================================

/**
 * List SEO fields for all experiences in a farm (Admin)
 * GET /api/admin/farms/:farmId/experiences/seo
 * @param {number} farmId
 * @returns {Promise<Array<{ id: number, metaTitle: string, metaDescription: string }>>}
 */
export const listExperienceSeoByFarmIdAdmin = async (farmId) => {
  return client.get(`/api/admin/farms/${farmId}/experiences/seo`);
};

/**
 * Get SEO fields for a single experience (Admin)
 * GET /api/admin/experiences/:id/seo
 * @param {number} experienceId
 * @returns {Promise<{ metaTitle: string, metaDescription: string }>}
 */
export const getExperienceSeoByIdAdmin = async (experienceId) => {
  return client.get(`/api/admin/experiences/${experienceId}/seo`);
};

/**
 * Update SEO fields for a single experience (Admin)
 * PATCH /api/admin/experiences/:id/seo
 * @param {number} experienceId
 * @param {{ metaTitle?: string, metaDescription?: string }} dto
 * @returns {Promise<{ metaTitle: string, metaDescription: string }>}
 */
export const updateExperienceSeoAdmin = async (experienceId, dto) => {
  return client.patch(`/api/admin/experiences/${experienceId}/seo`, dto);
};

